import React from 'react'
import type { JSX } from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { ApiProvider } from '@/hooks/useAppApi'
import { SentryUserSync } from '@/components/SentryUserSync'
import { PendoRoot } from '@/components/PendoRoot'
import { NotificationProviderWrapper } from '@/providers/NotificationProviderWrapper'
import { TemplatesMigrationDialog } from '@/features/import/components/TemplatesMigrationDialog'
import { LoginPage } from '@/pages/LoginPage'
import { UnauthorizedPage } from '@/pages/UnauthorizedPage'
import { DashboardPage } from '@/pages/DashboardPage'
import { WelcomePage } from '@/pages/WelcomePage'
import { AuthCallbackPage } from '@/features/auth/pages/AuthCallbackPage'
import { AppLayout } from '@/components/AppLayout'
import { getFeatureRoutes } from '@/features/registry'
import { Toaster } from '@/components/ui/sonner'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      retryDelay: (attemptIndex: number): number => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
    mutations: {
      retry: 1,
    },
  },
})

export const App = (): JSX.Element => {
  return (
    <QueryClientProvider client={queryClient}>
      <ApiProvider>
        <SentryUserSync />
        <PendoRoot>
        <NotificationProviderWrapper>
          <BrowserRouter>
            <Routes>
              {/* Legacy route — redirect to dashboard (stale bookmarks) */}
              <Route path="/import/editor" element={
                <ProtectedRoute>
                  <Navigate to="/dashboard" replace />
                </ProtectedRoute>
              } />
              {/* All other routes inside AppLayout (sidebar + top nav) */}
              <Route element={<AppLayout><Outlet /></AppLayout>}>
                {/* Public routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/unauthorized" element={<UnauthorizedPage />} />
                <Route path="/auth/callback" element={<AuthCallbackPage />} />

                {/* Protected routes */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <DashboardPage />
                    </ProtectedRoute>
                  }
                />

                {/* Import page — inside AppLayout for header/nav */}
                <Route path="/import" element={
                  <ProtectedRoute>
                    <React.Suspense fallback={<div className="flex h-screen items-center justify-center">Loading...</div>}>
                      {React.createElement(React.lazy(() => import('@/features/import/pages/UploadPage')))}
                    </React.Suspense>
                  </ProtectedRoute>
                } />

                {/* Editor for persisted models — inside AppLayout for header/nav */}
                <Route path="/projects/:projectId/models/:modelId" element={
                  <ProtectedRoute>
                    <React.Suspense fallback={<div className="flex h-screen items-center justify-center">Loading...</div>}>
                      {React.createElement(React.lazy(() => import('@/features/import/pages/EditorPage')))}
                    </React.Suspense>
                  </ProtectedRoute>
                } />

                {/* Settings routes - requires admin:* permission */}
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute requiredPermissions={['admin:*']}>
                      <div className="p-6">
                        <h1 className="text-2xl font-bold mb-4">Account Settings</h1>
                        <p className="text-muted-foreground">
                          Manage your account and organization settings.
                        </p>
                      </div>
                    </ProtectedRoute>
                  }
                />

                {/* Feature routes (admin, profile, docs, dev testing) */}
                {getFeatureRoutes()}

                {/* Default route */}
                <Route path="/" element={<WelcomePage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
          </BrowserRouter>
          {/* FR-003 Tier 2: one-time localStorage → backend migration.
              Fires only when legacy templates exist and the user hasn't
              already chosen to migrate or dismiss. */}
          <TemplatesMigrationDialog />
        </NotificationProviderWrapper>
        </PendoRoot>
      </ApiProvider>
      <Toaster />
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}
